import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '@/App.css';
import './i18n/config';
import { supportedLanguageCodes, defaultLanguage } from './i18n/config';
import { LanguageSync, LanguageRedirect } from './components/LanguageRouter';

// Critical components loaded immediately
import Navbar from './components/Navbar';
import Footer from './components/Footer';
// Using optimized homepage for better PageSpeed - loaded immediately for fast FCP
import Homepage from './pages/HomepageOptimized';

// Lazy load heavy components that are not critical for first paint
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const BolognaConcierge = lazy(() => import('./components/BolognaConcierge'));

// Lazy load non-critical pages for better performance
const ExperiencesPage = lazy(() => import('./pages/ExperiencesPage'));
const ExperienceDetailPage = lazy(() => import('./pages/ExperienceDetailPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const BookingVoucherPage = lazy(() => import('./pages/BookingVoucherPage'));
const CheckinPage = lazy(() => import('./pages/CheckinPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePage = lazy(() => import('./pages/CookiePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Blog Pages (lazy loaded)
const BlogListingPage = lazy(() => import('./pages/BlogPages').then(m => ({ default: m.BlogListingPage })));
const BlogArticlePage = lazy(() => import('./pages/BlogPages').then(m => ({ default: m.BlogArticlePage })));
const BlogCategoryPage = lazy(() => import('./pages/BlogPages').then(m => ({ default: m.BlogCategoryPage })));

// SEO Editorial Pages (legacy - kept for backward compatibility)
const CosaFareTramonto = lazy(() => import('./pages/SEOPages').then(m => ({ default: m.CosaFareTramonto })));
const CookingClassBologna = lazy(() => import('./pages/SEOPages').then(m => ({ default: m.CookingClassBologna })));
const AperitivoPanoramico = lazy(() => import('./pages/SEOPages').then(m => ({ default: m.AperitivoPanoramico })));

// Gift Experience Page
const GiftExperiencePage = lazy(() => import('./pages/GiftExperiencePage'));

// Private Minibus Rental Page
const PrivateMinibusPage = lazy(() => import('./pages/PrivateMinibusPage'));

// Loading fallback - Hero-like static placeholder for better perceived performance
const PageLoader = () => (
  <div className="min-h-screen bg-cream-50">
    {/* Static hero placeholder */}
    <div className="relative min-h-[90vh] bg-gradient-to-br from-gray-800 to-gray-900 flex items-center">
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="max-w-2xl animate-pulse">
          <div className="h-8 w-40 bg-white/20 rounded-full mb-6" />
          <div className="h-16 w-full bg-white/20 rounded mb-4" />
          <div className="h-16 w-3/4 bg-white/20 rounded mb-8" />
          <div className="h-6 w-2/3 bg-white/10 rounded mb-8" />
          <div className="flex gap-4">
            <div className="h-14 w-48 bg-red-600/50 rounded-full" />
            <div className="h-14 w-40 bg-white/10 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Mobile sticky CTA component with language support
// Hidden on experience detail pages (they have their own booking CTA)
// On experiences list page -> scrolls to products instead of redirecting
const MobileStickyBookCTA = () => {
  const [show, setShow] = React.useState(false);
  const { t } = useTranslation();
  const { lang } = useParams();
  const location = window.location.pathname;

  React.useEffect(() => {
    const handleScroll = () => {
      setShow(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide on experience detail pages (they have their own booking module)
  const isExperienceDetailPage = location.includes('/experience/');
  // Check if on experiences list page
  const isExperiencesListPage = location.includes('/experiences') && !isExperienceDetailPage;
  
  if (!show || isExperienceDetailPage) return null;

  const currentLang = supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;

  // On experiences page, scroll to products section instead of redirecting
  const handleClick = (e) => {
    if (isExperiencesListPage) {
      e.preventDefault();
      const productsSection = document.querySelector('[data-testid="products-section"]') || 
                              document.querySelector('.experience-card');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    // Otherwise, let the <a> tag navigate normally
  };

  return (
    <div className="mobile-sticky-cta fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-gray-200 p-4 z-50 shadow-lg">
      <a
        href={`/${currentLang}/experiences`}
        onClick={handleClick}
        className="block w-full bg-red-600 text-white text-center py-3 rounded-xl font-medium"
      >
        {isExperiencesListPage 
          ? (currentLang === 'it' ? 'Scopri le Esperienze' : t('common.book_now'))
          : t('common.book_now')
        }
      </a>
    </div>
  );
};

// Layout component with language sync
const PublicLayout = () => {
  return (
    <LanguageSync>
      <Navbar />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/experiences" element={<ExperiencesPage />} />
          <Route path="/experience/:slug" element={<ExperienceDetailPage />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/voucher/:reference" element={<BookingVoucherPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          
          {/* SEO Editorial Pages / Legacy Blog Routes */}
          <Route path="/blog/cosa-fare-bologna-tramonto" element={<CosaFareTramonto />} />
          <Route path="/blog/cooking-class-bologna" element={<CookingClassBologna />} />
          <Route path="/blog/aperitivo-panoramico-bologna" element={<AperitivoPanoramico />} />
          
          {/* New Blog System */}
          <Route path="/blog" element={<BlogListingPage />} />
          <Route path="/blog/categoria/:category" element={<BlogCategoryPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          
          {/* SEO Landing Pages */}
          <Route path="/cosa-fare-a-bologna" element={<Navigate to="./blog/cosa-fare-a-bologna-in-un-giorno" replace />} />
          <Route path="/esperienze-bologna" element={<Navigate to="./experiences" replace />} />
          <Route path="/tour-bologna" element={<Navigate to="./blog/tour-panoramico-bologna" replace />} />
          <Route path="/cooking-class-bologna" element={<Navigate to="./blog/cooking-class-bologna-tortellini" replace />} />
          <Route path="/aperitivo-bologna" element={<Navigate to="./blog/aperitivo-panoramico-bologna" replace />} />
          <Route path="/bologna-al-tramonto" element={<Navigate to="./blog/cosa-fare-a-bologna-al-tramonto" replace />} />
          
          {/* City Red Bus Page */}
          <Route path="/city-red-bus" element={<Navigate to="./blog/tour-panoramico-bologna" replace />} />
          
          {/* Gift Experience Pages - multilingual URLs */}
          <Route path="/regala-esperienze-bologna" element={<GiftExperiencePage />} />
          <Route path="/gift-experiences-bologna" element={<GiftExperiencePage />} />
          <Route path="/offrir-experiences-bologne" element={<GiftExperiencePage />} />
          <Route path="/geschenk-erlebnisse-bologna" element={<GiftExperiencePage />} />
          <Route path="/regalar-experiencias-bolonia" element={<GiftExperiencePage />} />
          <Route path="/gift" element={<GiftExperiencePage />} />
          
          {/* Private Minibus Rental Pages - multilingual URLs */}
          <Route path="/noleggio-privato-minibus-bologna" element={<PrivateMinibusPage />} />
          <Route path="/private-tour-bologna-electric-minibus" element={<PrivateMinibusPage />} />
          <Route path="/location-minibus-prive-bologne" element={<PrivateMinibusPage />} />
          <Route path="/privater-minibus-verleih-bologna" element={<PrivateMinibusPage />} />
          <Route path="/alquiler-minibus-privado-bolonia" element={<PrivateMinibusPage />} />
          <Route path="/private-minibus" element={<PrivateMinibusPage />} />
          
          {/* Italian URL redirects for SEO - redirect to same lang path */}
          <Route path="/esperienze" element={<Navigate to="../experiences" replace />} />
          <Route path="/chi-siamo" element={<Navigate to="../about" replace />} />
          <Route path="/contatti" element={<Navigate to="../contact" replace />} />
          <Route path="/prenotazione" element={<Navigate to="../booking" replace />} />
          
          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund" element={<RefundPage />} />

          {/* Catch all within language - redirect to language home */}
          <Route path="*" element={<Navigate to="" replace />} />
        </Routes>
      </main>
      <Footer />
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
      <MobileStickyBookCTA />
      <Suspense fallback={null}>
        <BolognaConcierge />
      </Suspense>
    </LanguageSync>
  );
};

function App() {
  // Remove initial loader after app mounts
  useEffect(() => {
    const loader = document.getElementById('initial-loader');
    if (loader) {
      loader.style.opacity = '0';
      loader.style.transition = 'opacity 0.3s ease';
      setTimeout(() => loader.remove(), 300);
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Admin Routes - No Navbar/Footer, no language prefix */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            
            {/* Operator Check-in Route - No Navbar/Footer, no language prefix */}
            <Route path="/checkin/:reference" element={<CheckinPage />} />

            {/* Root redirect to default language */}
            <Route path="/" element={<LanguageRedirect />} />

            {/* Language-prefixed routes */}
            <Route path="/:lang/*" element={<PublicLayout />} />

            {/* Fallback - redirect to default language */}
            <Route path="*" element={<Navigate to={`/${defaultLanguage}`} replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
